import { useState } from 'react';
import { Sparkles, Loader2, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';
import { useLeadEnrichmentStatus } from '../../lib/enrichment/hooks';
import { ProvenanceList } from './ProvenanceList';

interface Props {
  leadId: string;
}

/**
 * Card mostrata in scheda lead/cliente — estetica Notion (compatta, monocromatica).
 */
export function EnrichmentLeadCard({ leadId }: Props) {
  const { runs, provenance, loading, error, reload, trigger } = useLeadEnrichmentStatus(leadId);
  const [triggering, setTriggering] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [triggerMessage, setTriggerMessage] = useState<string | null>(null);

  const onTrigger = async () => {
    setTriggering(true);
    setTriggerMessage(null);
    try {
      const r = await trigger();
      if (r.enqueued) {
        setTriggerMessage('Accodato, attendi qualche secondo…');
      } else {
        setTriggerMessage(friendlyReason(r.reason));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setTriggerMessage(`Errore: ${msg}`);
    } finally {
      setTriggering(false);
    }
  };

  const backendDown = !!error && !loading;
  const inProgress = runs.some((r) => r.status === 'in_progress' || r.status === 'queued');
  const lastCompleted = runs.find((r) => r.status === 'completed');
  const hasErrors = runs.some((r) => r.status === 'failed');
  const hasData = provenance.length > 0;

  const statusLabel = backendDown
    ? 'servizio non disponibile'
    : inProgress
      ? 'in corso…'
      : hasData
        ? 'arricchito'
        : hasErrors
          ? 'errori'
          : 'non arricchito';

  const dotColor = backendDown
    ? 'bg-[rgba(55,53,47,0.16)]'
    : inProgress
      ? 'bg-blue-400'
      : hasData
        ? 'bg-emerald-500'
        : hasErrors
          ? 'bg-red-400'
          : 'bg-gray-300';

  // Sezione flat in stile Notion, coerente con le altre proprieta' della colonna
  // (header uppercase muted + riga stato + azioni come link): niente box bianco.
  return (
    <div className="text-[13px]">
      {/* Header sezione (come SectionHeader del layout) */}
      <div className="flex items-center justify-between py-2 mb-1">
        <h3 className="text-xs font-medium text-[#787774] dark:text-gray-400 uppercase tracking-wide">
          Arricchimento
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => void reload()}
            disabled={loading}
            className="p-1 text-[#787774] hover:bg-[rgba(55,53,47,0.06)] dark:hover:bg-white/[0.06] rounded transition-colors disabled:opacity-50"
            title="Ricarica"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onTrigger}
            disabled={triggering || inProgress || backendDown}
            className="text-xs text-[#337EA9] dark:text-[#529CCA] hover:underline flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed disabled:no-underline"
            title={backendDown ? 'Backend non disponibile' : undefined}
          >
            {triggering && <Loader2 className="w-3 h-3 animate-spin" />}
            Arricchisci
          </button>
        </div>
      </div>

      {/* Riga stato (come PropertyRow) */}
      <div className="flex items-center justify-between py-2 px-2 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors">
        <div className="flex items-center space-x-2.5">
          <Sparkles className="h-4 w-4 text-[#787774] dark:text-gray-500 flex-shrink-0" />
          <span className="text-sm text-[#787774] dark:text-gray-400">Stato</span>
        </div>
        <span className="flex items-center gap-1.5 text-sm text-[#37352F] dark:text-white">
          <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`} aria-hidden />
          {statusLabel}
          {lastCompleted?.completed_at && (
            <span className="text-[#9b9a97]">
              · {new Date(lastCompleted.completed_at).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' })}
            </span>
          )}
        </span>
      </div>

      {/* Trigger message / backend down */}
      {(triggerMessage || backendDown) && (
        <div className="px-2 pb-1 text-[12px] text-[#787774] dark:text-gray-400">
          {backendDown && !triggerMessage
            ? 'Servizio enrichment in fase di deploy. Riprova fra qualche minuto o contatta il superadmin.'
            : triggerMessage}
        </div>
      )}

      {/* Provenance */}
      {hasData && (
        <>
          <button
            onClick={() => setExpanded((v) => !v)}
            className="w-full px-2 py-1.5 flex items-center gap-1 text-[12px] text-[#787774] dark:text-gray-400 hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.03] rounded transition-colors"
          >
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {provenance.length} {provenance.length === 1 ? 'campo arricchito' : 'campi arricchiti'}
          </button>
          {expanded && (
            <div className="px-2 pb-2">
              <ProvenanceList provenance={provenance} onReload={reload} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function friendlyReason(reason?: string): string {
  switch (reason) {
    case 'master_disabled':
      return 'Arricchimento disattivato in impostazioni';
    case 'source_disabled':
      return 'Sorgente disabilitata in impostazioni';
    case 'settings_missing':
      return 'Impostazioni non configurate';
    case 'enqueue_error':
      return 'Errore tecnico nella coda';
    default:
      return reason ?? 'sconosciuto';
  }
}
